// Shared "Enrollment Pending Approval" card. Shown to the employee on /benefits
// (read-only status) and to the broker/employer on the employee profile (with a
// "Review request" button linking to the approval task). One component so the
// two views can't drift.
import type { ReactNode } from "react";
import { Link } from "wouter";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, ArrowRight } from "lucide-react";
import { getTypeIcon } from "@/components/benefits/v2/typeIcons";
import { getWaiveReasonLabel } from "@shared/utils/waiveReasons";
import { parseDateLocal } from "@/lib/utils";

interface PendingEnrollmentRequestCardProps {
  /** The requestedEnrollments from the enrollment-request task's customFields. */
  elections: any[];
  /** The requestedWaivers (plans the employee declined). */
  waivers?: any[];
  effectiveDate?: string | null;
  /** Plans list, to resolve each election's name/type/icon. */
  plans?: any[];
  /** When set, renders a "Review request" button linking here (broker/employer). */
  reviewHref?: string;
  /** Override the description line (default is the employee-facing wording). */
  description?: ReactNode;
}

const tierLabelFor = (tier: string | null | undefined) =>
  String(tier || "employee_only")
    .replace("employee_only", "Employee Only")
    .replace("employee_spouse", "Employee + Spouse")
    .replace("employee_children", "Employee + Child(ren)")
    .replace("family", "Family")
    .replace(/_/g, " ");

export function PendingEnrollmentRequestCard({
  elections,
  waivers = [],
  effectiveDate,
  plans,
  reviewHref,
  description,
}: PendingEnrollmentRequestCardProps) {
  const hasRows = elections.length > 0 || waivers.length > 0;
  return (
    <Card className="relative border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950">
      <div className="pointer-events-none absolute left-0 top-0 h-full w-1.5 bg-amber-500 rounded-l-md" />
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-amber-900 dark:text-amber-100">
              <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              Enrollment Pending Approval
            </CardTitle>
            <CardDescription className="text-amber-700 dark:text-amber-300">
              {description ?? "Your benefit elections are being reviewed by your broker."}
              {effectiveDate && (
                <>
                  {" "}
                  Effective date:{" "}
                  {(parseDateLocal(effectiveDate) ?? new Date(effectiveDate)).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </>
              )}
            </CardDescription>
          </div>
          {reviewHref && (
            <Button asChild size="sm" className="shrink-0">
              <Link href={reviewHref}>
                Review request <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          )}
        </div>
      </CardHeader>
      {hasRows && (
        <CardContent>
          <div className="space-y-2">
            {elections.map((e: any, i: number) => {
              const plan = (plans as any[])?.find((p: any) => p.id === e.planId);
              const Icon = getTypeIcon(plan?.type || e.benefitType);
              return (
                <div
                  key={`enroll-${i}`}
                  className="flex items-center justify-between rounded-lg border border-amber-200 dark:border-amber-700 px-4 py-3 bg-white dark:bg-gray-800"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-md">
                      <Icon className="h-4 w-4 text-amber-700 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {plan?.name || e.planName || `Plan #${e.planId}`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {tierLabelFor(e.coverageTier)}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-amber-700 border-amber-300">
                    Pending
                  </Badge>
                </div>
              );
            })}
            {waivers.map((w: any, i: number) => {
              const plan = (plans as any[])?.find((p: any) => p.id === w.planId);
              const Icon = getTypeIcon(plan?.type || w.benefitType);
              return (
                <div
                  key={`waive-${i}`}
                  className="flex items-center justify-between rounded-lg border border-stone-200 dark:border-stone-700 px-4 py-3 bg-white/60 dark:bg-gray-800/60"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-stone-100 dark:bg-stone-800 rounded-md">
                      <Icon className="h-4 w-4 text-stone-500" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-stone-500 line-through">
                        {plan?.name || w.planName || `Plan #${w.planId}`}
                      </p>
                      {w.waiverReason && (
                        <p className="text-xs text-muted-foreground">
                          {getWaiveReasonLabel(w.waiverReason)}
                        </p>
                      )}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-stone-500 border-stone-300">
                    Waived
                  </Badge>
                </div>
              );
            })}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
